import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ZalamoDialogService } from '../../zalamo-dialog/zalamo-dialog.service';
import { PhotoSelectionComponent } from '../photo-selection/photo-selection.component';
import { SelectPhotoPositionComponent } from '../select-photo-position/select-photo-position.component';
import { first, takeUntil } from 'rxjs/operators';
import { UploadService } from '../../../upload/upload.service';
import { BehaviorSubject, combineLatest, from } from 'rxjs';
import { UploadContext } from 'Upload/upload/graphql';
import { FullscreenService } from '../../fullscreen/fullscreen.service';
import { OrganizationService } from '../../../core/organizations/organization.service';
import {
  ImageMetadataService,
  isUnsupportedJpegColorSpaceError,
} from '../../../core/image/image-metadata.service';
import { ButtonComponent } from 'ZalamoLibrary/src/lib/ui/button/button.component';
import { translationProvider } from '@ui/nls/translations/translate.directive';

@Component({
    selector: 'lib-setting-cover',
    templateUrl: './setting-cover.component.html',
    styleUrls: ['./setting-cover.component.scss'],
    providers: [translationProvider('publicSessionEditor')],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettingCoverComponent implements OnInit {
  @Input() @HostBinding('class.forceSquare') forceSquare = false;
  @Input() @HostBinding('class.isSelected') selectedPhoto = false;
  @Input() url;
  @Input() imageSize;
  @Input() coverType: 'public' | 'private' | 'booking' = 'public';
  @Input() context;
  @Input() contextId;
  @Output() reset = new EventEmitter();
  acceptedFormats = ['image/jpeg', 'image/png'];
  isUploading$ = new BehaviorSubject(false);
  @Output() saved = new BehaviorSubject<{
    x: number;
    y: number;
    file: string;
    size: {
      w: number;
      h: number;
    };
  }>(null);
  @ViewChild('menuTrigger', { static: false })
  menuTrigger: ButtonComponent;
  selectedPoint = true;
  private _point: {
    x: number;
    y: number;
    file: string;
    size: { w: number; h: number };
  };

  get point(): {
    x: number;
    y: number;
    file: string;
    size: { w: number; h: number };
  } {
    return this._point;
  }

  @Input() set point(value: {
    x: number;
    y: number;
    file: string;
    size: { w: number; h: number };
  }) {
    this._point = value;
    if (value.size) {
      this.imageSize = value.size;
    }
    this.selectedPhoto = false;
    console.log('DOWNLOAD FILE', value);
    if (value.file) {
      this.uploadService
        .getSingleFileUrl(value.file, { h: 2048, w: 2048 })
        .pipe(first())
        .subscribe((url) => {
          console.log(url);
          this.url = url;
          this.selectedPhoto = true;
        });
      this.cd.detectChanges();
    }
  }

  constructor(
    private dialog: ZalamoDialogService,
    private uploadService: UploadService,
    private cd: ChangeDetectorRef,
    private fullscreenService: FullscreenService,
    private organizationService: OrganizationService,
    private metadata: ImageMetadataService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  @HostListener(':click', ['$event']) onClick(ev) {
    if (this.menuTrigger?.contains(ev.target)) {
      return false;
    }
    if (this.selectedPhoto || this.contextId) {
      this.selectedPhoto
        ? this.openFullPhotoEdit()
        : this.openFullPhotoSelection();
    }
  }

  ngOnInit(): void {}

  edit() {
    this.selectedPhoto
      ? this.openFullPhotoEdit()
      : this.openFullPhotoSelection();
  }

  openFullPhotoSelection() {
    const ref = this.dialog.openFull(PhotoSelectionComponent);
    ref.componentInstance.contextId = this.contextId;
    ref.componentInstance.context = this.context;
    ref.componentInstance.coverType = this.coverType;
    ref.componentInstance.filePicked
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(({ fileName, size }) => {
        console.log('FILE ', fileName);
        this.saved.next({ file: fileName, size, x: 50, y: 50 });
        this._point = {
          x: 50,
          y: 50,
          file: fileName,
          size,
        };
        this.uploadService
          .getSingleFileUrl(fileName, { h: 2048, w: 2048 })
          .pipe(first())
          .subscribe((url) => {
            console.log(url);
            this.url = url;
            this.cd.detectChanges();
            this.imageSize = size;
            this.selectedPoint = false;
            this.openFullPhotoEdit()
              .afterOpened()
              .toPromise()
              .then(() => {
                ref.close();
              });
          });
      });
  }

  openFullPhotoEdit() {
    const ref = this.dialog.openFull(SelectPhotoPositionComponent);
    const { componentInstance } = ref;
    componentInstance.url = this.url;
    componentInstance.isBooking = this.coverType === 'booking';
    componentInstance.imageSize = this.imageSize;
    componentInstance.x = this._point?.x;
    componentInstance.y = this._point?.y;
    componentInstance.selectedPoint = this.selectedPoint;
    componentInstance.fileName = this.point.file;
    componentInstance.context = this.context;
    componentInstance.contextId = this.contextId;
    ref.componentInstance.saved
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe(({ y, url, size, x, file }) => {
        this.saved.next({
          size,
          x,
          y,
          file,
        });

        this.url = url;
        this.imageSize = size;
        this._point = {
          x,
          y,
          file,
          size,
        };

        if (this.url) {
          this.selectedPhoto = true;
        }
        ref.close();
        this.cd.detectChanges();
      });
    return ref;
  }

  uploadFromDisk(input: HTMLInputElement) {
    if (this.acceptedFormats.indexOf(input.files[0].type) === -1) {
      this.fullscreenService.open({
        content: 'Okładka obsługuje tylko pliki jpeg i png',
      });
      return;
    }

    this.isUploading$.next(true);
    combineLatest([
      this.uploadService.uploadSingleFile(
        input.files[0],
        this.coverType === 'public'
          ? UploadContext.CoverPublic
          : this.coverType === 'private'
          ? UploadContext.CoverPrivate
          : UploadContext.CoverBooking,
        this.contextId,
        this.organizationService.lastOrganizationId,
      ),
      from(this.metadata.getDetails(input.files[0])),
    ])
      .pipe(first())
      .subscribe({
        next: ([fileName, details]) => {
          console.log(fileName, details);

          this.uploadService
            .getSingleFileUrl(fileName, { h: 2048, w: 2048 })
            .pipe(first())
            .subscribe((url) => {
              const size = {
                w: !!details.file
                  ? details.file['Image Width'].value
                  : details.pngFile['Image Width'].value,
                h: !!details.file
                  ? details.file['Image Height'].value
                  : details.pngFile['Image Height'].value,
              };
              this.point = {
                x: 50,
                y: 50,
                size,
                file: fileName,
              };
              this.isUploading$.next(false);
              this.selectedPoint = false;
              this.openFullPhotoEdit();
            });
        },
        error: (error) => {
          this.isUploading$.next(false);
          if (!isUnsupportedJpegColorSpaceError(error)) {
            console.error(error);
          }
        },
      });
  }
}

